import React, { useCallback, useRef, useState } from 'react';
import { useCreateMediaMutation } from '../redux/api/mediaApi';
import { MediaPurpose } from '../common/enums/MediaPurpose.enum';
import { getMediaType } from '../utils/getMediaType';

interface UseMarkdownImageUploadProps {
  value: string;
  onChange: (next: string) => void;
  purpose?: MediaPurpose;
}

export const useMarkdownImageUpload = ({
  value,
  onChange,
  purpose = MediaPurpose.General,
}: UseMarkdownImageUploadProps) => {
  const [createMedia] = useCreateMediaMutation();
  const [isUploading, setIsUploading] = useState(false);

  // Always-fresh refs so async callbacks don't capture stale closures
  const valueRef = useRef(value);
  valueRef.current = value;
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  const uploadImageFiles = useCallback(
    async (files: File[], insertionPoint: number) => {
      const imageFiles = files.filter((f) => f.type.startsWith('image/'));
      if (imageFiles.length === 0) return;

      setIsUploading(true);

      // inFlight tracks our own mutations to avoid reading stale React state
      // between concurrent swap callbacks (JS is single-threaded so this is safe)
      const inFlight = { current: valueRef.current };
      const safeUpdate = (next: string) => {
        inFlight.current = next;
        onChangeRef.current(next);
      };

      // Insert all placeholders before any upload starts
      let offset = insertionPoint;
      const placeholders: string[] = [];
      for (let i = 0; i < imageFiles.length; i++) {
        const nonce = Math.random().toString(36).slice(2, 8);
        const placeholder = `![uploading-${nonce}]()`;
        placeholders.push(placeholder);
        safeUpdate(
          inFlight.current.slice(0, offset) +
            placeholder +
            inFlight.current.slice(offset)
        );
        offset += placeholder.length;
      }

      await Promise.all(
        imageFiles.map(async (file, idx) => {
          const placeholder = placeholders[idx];
          try {
            const fd = new FormData();
            fd.append('file', file);
            fd.append('mediaType', getMediaType(file.type));
            fd.append('purpose', purpose);
            // @ts-expect-error — RTK Query mutation types don't accept FormData directly
            const res = await createMedia(fd).unwrap();
            const latest = inFlight.current;
            const pidx = latest.indexOf(placeholder);
            if (pidx >= 0) {
              safeUpdate(
                latest.slice(0, pidx) +
                  `![](${res.url})` +
                  latest.slice(pidx + placeholder.length)
              );
            }
          } catch {
            const latest = inFlight.current;
            const pidx = latest.indexOf(placeholder);
            if (pidx >= 0) {
              safeUpdate(
                latest.slice(0, pidx) + latest.slice(pidx + placeholder.length)
              );
            }
          }
        })
      );

      setIsUploading(false);
    },
    [createMedia, purpose]
  );

  const onPaste = useCallback(
    (e: React.ClipboardEvent<HTMLTextAreaElement>) => {
      const files = Array.from(e.clipboardData.files);
      const imageFiles = files.filter((f) => f.type.startsWith('image/'));
      if (imageFiles.length === 0) return;
      e.preventDefault();
      const pos = e.currentTarget.selectionStart ?? valueRef.current.length;
      uploadImageFiles(imageFiles, pos);
    },
    [uploadImageFiles]
  );

  const onDrop = useCallback(
    (e: React.DragEvent<HTMLTextAreaElement>) => {
      const files = Array.from(e.dataTransfer.files);
      const imageFiles = files.filter((f) => f.type.startsWith('image/'));
      if (imageFiles.length === 0) return;
      e.preventDefault();
      const pos = e.currentTarget.selectionStart ?? valueRef.current.length;
      uploadImageFiles(imageFiles, pos);
    },
    [uploadImageFiles]
  );

  const onDragOver = useCallback((e: React.DragEvent<HTMLTextAreaElement>) => {
    e.preventDefault();
  }, []);

  return {
    textareaProps: { onPaste, onDrop, onDragOver } as const,
    isUploading,
  };
};
